import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import {
  adjustEndKeepingStart,
  adjustStartKeepingEnd,
  formatAudioTimeMs,
  formatDurationSecondsMs,
  moveSelectionKeepingDuration,
  selectionDurationMs,
  type TrimSelectionMs,
} from './audioTrimTime'

export type WaveformDragMode = 'move' | 'start' | 'end' | null

/** VistoeraTASK-00171 §五 — imperative playhead API: `AudioEditorModal`
 * pushes the current playback position here on every `timeupdate`/rAF
 * tick WITHOUT touching this component's React props, so the (up to
 * ~1200-bar) peaks SVG never re-renders/reconciles at playback frame
 * rate — only the single lightweight playhead `<div>` moves, via direct
 * DOM style writes (spec §五 "不要使用高频 React 全树 rerender"). */
export type AudioEditorWaveformHandle = {
  setPlayheadRatio: (ratio: number | null) => void
}

type Props = {
  peaks: number[]
  durationMs: number
  selection: TrimSelectionMs
  onSelectionChange: (next: TrimSelectionMs) => void
  disabled?: boolean
}

function msToRatio(ms: number, durationMs: number): number {
  if (durationMs <= 0) return 0
  return Math.min(1, Math.max(0, ms / durationMs))
}

// Memoized — the props driving this (peaks/selection/durationMs) only
// change on load or on an actual selection edit, never at playback
// frame rate (playhead motion is 100% imperative, see the handle above).
const AudioEditorWaveform = memo(
  forwardRef<AudioEditorWaveformHandle, Props>(function AudioEditorWaveform(
    { peaks, durationMs, selection, onSelectionChange, disabled },
    ref,
  ) {
    const rootRef = useRef<HTMLDivElement | null>(null)
    const playheadRef = useRef<HTMLDivElement | null>(null)
    const dragRef = useRef<{
      mode: WaveformDragMode
      pointerId: number
      durationAtDrag: number
      anchorMs: number
      selectionStartAtDrag: number
    } | null>(null)
    const [dragging, setDragging] = useState<WaveformDragMode>(null)

    useImperativeHandle(
      ref,
      () => ({
        setPlayheadRatio: (ratio) => {
          const el = playheadRef.current
          if (!el) return
          if (ratio == null) {
            el.style.display = 'none'
            return
          }
          el.style.display = 'block'
          el.style.left = `${Math.min(1, Math.max(0, ratio)) * 100}%`
        },
      }),
      [],
    )

    const duration = selectionDurationMs(selection)

    const ticks = useMemo(() => {
      if (durationMs <= 0) return []
      const stepMs = durationMs <= 120_000 ? 30_000 : durationMs <= 600_000 ? 60_000 : 300_000
      const out: number[] = []
      for (let t = 0; t < durationMs; t += stepMs) out.push(t)
      out.push(durationMs)
      return out
    }, [durationMs])

    const clientXToMs = useCallback(
      (clientX: number) => {
        const el = rootRef.current
        if (!el || durationMs <= 0) return 0
        const rect = el.getBoundingClientRect()
        const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
        return Math.round(ratio * durationMs)
      },
      [durationMs],
    )

    const onPointerDown = useCallback(
      (mode: WaveformDragMode, e: ReactPointerEvent) => {
        if (disabled || !mode) return
        e.preventDefault()
        e.stopPropagation()
        ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
        dragRef.current = {
          mode,
          pointerId: e.pointerId,
          durationAtDrag: duration,
          anchorMs: clientXToMs(e.clientX),
          selectionStartAtDrag: selection.startMs,
        }
        setDragging(mode)
      },
      [clientXToMs, disabled, duration, selection.startMs],
    )

    const onPointerMove = useCallback(
      (e: ReactPointerEvent) => {
        const drag = dragRef.current
        if (!drag || drag.pointerId !== e.pointerId) return
        const ms = clientXToMs(e.clientX)
        if (drag.mode === 'move') {
          const delta = ms - drag.anchorMs
          onSelectionChange(
            moveSelectionKeepingDuration(drag.selectionStartAtDrag + delta, drag.durationAtDrag, durationMs),
          )
        } else if (drag.mode === 'start') {
          onSelectionChange(adjustStartKeepingEnd(ms, selection.endMs, durationMs))
        } else if (drag.mode === 'end') {
          onSelectionChange(adjustEndKeepingStart(selection.startMs, ms, durationMs))
        }
      },
      [clientXToMs, durationMs, onSelectionChange, selection.endMs, selection.startMs],
    )

    const onPointerUp = useCallback((e: ReactPointerEvent) => {
      const drag = dragRef.current
      if (!drag || drag.pointerId !== e.pointerId) return
      dragRef.current = null
      setDragging(null)
      try {
        ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
      } catch {
        /* ignore */
      }
    }, [])

    const startRatio = msToRatio(selection.startMs, durationMs)
    const endRatio = msToRatio(selection.endMs, durationMs)

    const hasPeaks = peaks.length > 0

    return (
      <div className="pcv-audio-editor-waveform-wrap">
        <div className="pcv-audio-editor-time-ruler" aria-hidden>
          {ticks.map((t) => (
            <span key={t} style={{ left: `${msToRatio(t, durationMs) * 100}%` }}>
              {formatAudioTimeMs(t).replace(/\.0$/, '')}
            </span>
          ))}
        </div>
        <div
          ref={rootRef}
          className={`pcv-audio-editor-waveform${dragging ? ' is-dragging' : ''}${!hasPeaks ? ' is-fallback' : ''}`}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid="audio-editor-waveform"
        >
          {hasPeaks ? (
            <svg className="pcv-audio-editor-waveform-svg" viewBox={`0 0 ${peaks.length} 100`} preserveAspectRatio="none">
              {peaks.map((p, i) => {
                const h = Math.max(2, p * 90)
                const x = i
                const muted = i / peaks.length < startRatio || i / peaks.length > endRatio
                return (
                  <rect
                    key={i}
                    x={x}
                    y={50 - h / 2}
                    width={1.05}
                    height={h}
                    className={muted ? 'pcv-audio-editor-wave-bar muted' : 'pcv-audio-editor-wave-bar active'}
                  />
                )
              })}
            </svg>
          ) : (
            <div className="pcv-audio-editor-waveform-fallback-track" />
          )}

          <div
            className="pcv-audio-editor-selection"
            style={{ left: `${startRatio * 100}%`, width: `${(endRatio - startRatio) * 100}%` }}
            onPointerDown={(e) => onPointerDown('move', e)}
          >
            <button
              type="button"
              className="pcv-audio-editor-handle pcv-audio-editor-handle--start"
              aria-label="Trim start"
              onPointerDown={(e) => onPointerDown('start', e)}
            />
            <button
              type="button"
              className="pcv-audio-editor-handle pcv-audio-editor-handle--end"
              aria-label="Trim end"
              onPointerDown={(e) => onPointerDown('end', e)}
            />
          </div>

          {/* VistoeraTASK-00171 §五 — the actual PLAYBACK POSITION line,
           * visually distinct (amber) from the selection's white
           * start/end boundary lines so the two are never confused —
           * "selection" = trim range, this = where `audio.currentTime`
           * is right now. Starts hidden (`display:none` inline, set via
           * the imperative handle above) until the first real position
           * update arrives. */}
          <div ref={playheadRef} className="pcv-audio-editor-playhead" data-testid="audio-editor-playhead" style={{ display: 'none' }} />
        </div>
        <div className="pcv-audio-editor-selection-labels">
          <span>{formatAudioTimeMs(selection.startMs)}</span>
          <span className="pcv-audio-editor-selection-duration">
            ← {formatDurationSecondsMs(duration)} 秒 →
          </span>
          <span>{formatAudioTimeMs(selection.endMs)}</span>
        </div>
      </div>
    )
  }),
)

export default AudioEditorWaveform
